import Court from "../../entities/court";

/**
 * Court actions
 *
 * Call order:
 *
 * COURT_SAVE_QUEUE:	adds the item to the save queue
 * --> COURT_SAVE:	modifies the state
 * --> COURT_SAVE_SUCCESS: if all went well
 * --> COURT_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail COURT_SAVE_QUEUE is during the call to COURT_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at COURT_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: COURT_SAVE_SUCCESS, COURT_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or COURT_PENDING_CHECK (it left the queue).
 */

export const COURT_FETCH_ONE_QUEUE: any = 'COURT_FETCH_ONE_QUEUE';
export const COURT_FETCH_SUCCESS: any = 'COURT_FETCH_SUCCESS';
export const COURT_FETCH_FAILURE: any = 'COURT_FETCH_FAILURE';
export const COURT_FETCH_ONE_SUCCESS: any = 'COURT_FETCH_ONE_SUCCESS';
export const COURT_FETCH_ONE_FAILURE: any = 'COURT_FETCH_ONE_FAILURE';

export const COURT_SAVE: any = 'COURT_SAVE';
export const COURT_SAVE_QUEUE: any = 'COURT_SAVE_QUEUE';
export const COURT_SAVE_SUCCESS: any = 'COURT_SAVE_SUCCESS';
export const COURT_SAVE_FAILURE: any = 'COURT_SAVE_FAILURE';

export const COURT_SAVE_MANY: any = 'COURT_SAVE_MANY';
export const COURT_SAVE_MANY_QUEUE: any = 'COURT_SAVE_MANY_QUEUE';
export const COURT_SAVE_MANY_SUCCESS: any = 'COURT_SAVE_MANY_SUCCESS';
export const COURT_SAVE_MANY_FAILURE: any = 'COURT_SAVE_MANY_FAILURE';

export const COURT_REMOVE: any = 'COURT_REMOVE';
export const COURT_REMOVE_QUEUE: any = 'COURT_REMOVE_QUEUE';
export const COURT_REMOVE_SUCCESS: any = 'COURT_REMOVE_SUCCESS';
export const COURT_REMOVE_FAILURE: any = 'COURT_REMOVE_FAILURE';

export const COURT_RESTORE: any = 'COURT_RESTORE';
export const COURT_RESTORE_QUEUE: any = 'COURT_RESTORE_QUEUE';
export const COURT_RESTORE_SUCCESS: any = 'COURT_RESTORE_SUCCESS';
export const COURT_RESTORE_FAILURE: any = 'COURT_RESTORE_FAILURE';

export const COURT_RESTORE_MANY: any = 'COURT_RESTORE_MANY';
export const COURT_REMOVE_MANY: any = 'COURT_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const COURT_SAVE_REVERT: any = 'COURT_SAVE_REVERT';
export const COURT_PENDING_CHECK: any = 'COURT_PENDING_CHECK';

/* Fetch actions */
export const fetchCourtSuccess = (courts: Court[], partial: boolean, lastFetch: number) => ({type: COURT_FETCH_SUCCESS, courts, partial, lastFetch});
export const fetchCourtFailure = (err: any) => {
		console.error('CourtAction: fetchCourtFailure', err);
		return {type: COURT_FETCH_FAILURE, err};
};
export const fetchOneCourtQueue = (courtId: string, sentAddedOn?: number) => ({type: COURT_FETCH_ONE_QUEUE, courtId, sentAddedOn});
export const fetchOneCourtSuccess = (court: Court | null, sentAddedOn?: number, courtId: string = court?.id) => ({type: COURT_FETCH_ONE_SUCCESS, courtId, court, sentAddedOn});
export const fetchOneCourtFailure = (id: string, err: any) => {
		console.error('CourtAction: fetchOneCourtFailure', err);
		return {type: COURT_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveCourt = (court: Court) => {
    // console.debug('⮂ COURT_SAVE action', court?.id);
    return {type: COURT_SAVE, court};
}
export const saveCourtQueue = (court: Court, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ COURT_SAVE_QUEUE action', court?.id);
    return {type: COURT_SAVE_QUEUE, court, onQueued};
}
export const saveCourtSuccess = (court: Court, sentAddedOn?: number) => {
    // console.debug('⮂ COURT_SAVE_SUCCESS action', court?.id);
    return {type: COURT_SAVE_SUCCESS, court, sentAddedOn};
}
export const saveCourtFailure = (court: Court, status: number, message: string) => {
    console.warn(`⮂ COURT_SAVE_FAILURE error saving court: HTTP ${status}: ${message}`, court?.id);
    return {type: COURT_SAVE_FAILURE, court, status, message};
};

/* SaveMany actions */
export const saveManyCourts = (courts: Court[]) => {
    // console.debug('⮂ COURT_SAVE_MANY action', courts?.map(court => court?.id).join(','));
    return {type: COURT_SAVE_MANY, courts};
}
export const saveManyCourtsQueue = (courts: Court[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ COURT_SAVE_MANY_QUEUE action', courts?.map(court => court.id).join(','));
    return {type: COURT_SAVE_MANY_QUEUE, courts, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `courts` (OWA-1018) */
export const saveManyCourtsSuccess = (courts: Court[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ COURT_SAVE_MANY_SUCCESS action', courts?.map(court => court.id).join(','));
    return {type: COURT_SAVE_MANY_SUCCESS, courts, sentAddedOns};
}
export const saveManyCourtsFailure = (courts: Court[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_COURTS_FAILURE Error saving courts: HTTP ${status}: ${message}`, courts?.map(court => court.id).join(','));
		return {type: COURT_SAVE_MANY_FAILURE, courts, status, message};
};

/* Remove actions */
export const removeCourt = (court: Court) => ({ type: COURT_REMOVE, court });
export const removeCourtQueue = (court: Court) => ({ type: COURT_REMOVE_QUEUE, court });
export const removeCourtSuccess = (court: Court) => ({ type: COURT_REMOVE_SUCCESS, court });
export const removeCourtFailure = (court: Court, status: number, message: string) => {
	console.error(`Redux: removeCourtFailure Error removing court: HTTP ${status}: ${message}`);
	return ({ type: COURT_REMOVE_FAILURE, court, status, message });
};

/* Restore actions */
export const restoreCourt = (court: Court) => ({ type: COURT_RESTORE, court });
export const restoreCourtQueue = (court: Court) => ({ type: COURT_RESTORE_QUEUE, court });
export const restoreCourtSuccess = (court: Court, sentAddedOn?: number) => ({ type: COURT_RESTORE_SUCCESS, court, sentAddedOn });
export const restoreCourtFailure = (court: Court, status: number, message: string) => {
	console.error(`Redux: restoreCourtFailure Error restoring court: HTTP ${status}: ${message}`);
	return ({ type: COURT_RESTORE_FAILURE, court, status, message });
};

/* RestoreMany actions */
export const restoreManyCourts = (courts: Court[]) => ({ type: COURT_RESTORE_MANY, courts });

/* RemoveMany actions */
export const removeManyCourts = (courts: Court[]) => ({ type: COURT_REMOVE_MANY, courts });

/* Pending save actions (OWA-1018) */
/**
 * The save `court` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertCourtSave = (court: Court, options: {sentAddedOn?: number, current?: Court | null, keep?: boolean} = {}) =>
    ({type: COURT_SAVE_REVERT, court, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkCourtPendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: COURT_PENDING_CHECK, live, refused, readAt});